import React, { useEffect, useState } from 'react';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { TrackStatusDashboard } from './components/TrackStatusDashboard';
import { OperationsCharges } from './components/OperationsCharges';
import { OtherServicesDashboard } from './components/OtherServicesDashboard';
import { ServiceRegistration } from './components/ServiceRegistration';
import { ApplyService } from './components/ApplyService';
import { LoginScreen, LoggedInUser } from './components/LoginScreen';
import { WelcomeScreen } from './components/WelcomeScreen';
import { CheckInPopup } from './components/CheckInPopup';
import { CheckSquare } from 'lucide-react';
// ARMS imports
import { AppSidebar, PageType } from './components/AppSidebar';
import { AppHeader } from './components/AppHeader';
import { Dashboard } from './pages/Dashboard';
import { Roster } from './pages/Roster';
import { EmployeeAttendance } from './pages/EmployeeAttendance';
import { AttendanceManagement } from './pages/AttendanceManagement';
import { ManualAttendance } from './pages/ManualAttendance';
import { Payroll } from './pages/Payroll';
type ErpPage = 'dashboard' | 'operations' | 'otherServices' | 'serviceRegistration' | 'applyService';
export function App() {
  const [screen, setScreen] = useState<'login' | 'welcome' | 'erp' | 'arms'>('login');
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(null);
  const [erpPage, setErpPage] = useState<ErpPage>('dashboard');
  const [armsPage, setArmsPage] = useState<PageType>('dashboard');
  const [showCheckIn, setShowCheckIn] = useState(false);
  const handleLogin = (user: LoggedInUser) => {
    setLoggedInUser(user);
    setScreen('welcome');
  };
  const handleContinue = () => {
    setScreen('erp');
    // Show check-in popup after a brief delay
    setTimeout(() => setShowCheckIn(true), 800);
  };
  const handleErpMenuClick = (page: string) => {
    if (page === 'arms') {
      setScreen('arms');
      setArmsPage('dashboard');
      return;
    }
    const validPages: ErpPage[] = ['dashboard', 'operations', 'otherServices', 'serviceRegistration', 'applyService'];
    if (validPages.includes(page as ErpPage)) {
      setErpPage(page as ErpPage);
    } else {
      setErpPage('dashboard');
    }
  };
  const getErpPageTitle = () => {
    switch (erpPage) {
      case 'operations':
        return 'Operations Charges';
      case 'otherServices':
        return 'Other Services Billing';
      case 'serviceRegistration':
        return 'Service Registration';
      case 'applyService':
        return 'Apply Service';
      default:
        return 'Track Status Dashboard';
    }
  };
  const getArmsPageTitle = () => {
    switch (armsPage) {
      case 'dashboard':
        return 'Dashboard Overview';
      case 'roster':
        return 'Shift Roster';
      case 'attendance':
        return 'Employee Check-In';
      case 'manual':
        return 'Manual Attendance Entry';
      case 'payroll':
        return 'Payroll Management';
      case 'reports':
        return 'Reports & Analytics';
      case 'settings':
        return 'System Settings';
      default:
        return 'AttendEase';
    }
  };
  // Login Screen
  if (screen === 'login') {
    return <LoginScreen onLogin={handleLogin} data-id="element-0" />;
  }
  // Welcome Screen
  if (screen === 'welcome' && loggedInUser) {
    return <WelcomeScreen user={loggedInUser} onContinue={handleContinue} data-id="element-1" />;
  }
  // ARMS Portal
  if (screen === 'arms') {
    return <div className="flex h-screen w-full bg-gray-50 overflow-hidden font-sans" data-id="element-2">
        <AppSidebar activePage={armsPage} onNavigate={setArmsPage} data-id="element-3" />

        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-4">
          {/* ARMS Header with Back button */}
          <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-10" data-id="element-5">
            <div className="flex items-center gap-4" data-id="element-6">
              <button onClick={() => setScreen('erp')} className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors" data-id="element-7">
                ← Back to ERPLogiPark
              </button>
              <h2 className="text-xl font-semibold text-gray-800" data-id="element-8">
                {getArmsPageTitle()}
              </h2>
            </div>

            <div className="flex items-center gap-4" data-id="element-9">
              <div className="w-9 h-9 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center cursor-pointer" title={loggedInUser?.name || 'Admin'} data-id="element-10">
                <span className="text-sm font-bold text-indigo-700" data-id="element-11">
                  {loggedInUser?.name ? loggedInUser.name.substring(0, 2).toUpperCase() : 'AD'}
                </span>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto" data-id="element-12">
            {armsPage === 'dashboard' && <Dashboard data-id="element-13" />}
            {armsPage === 'roster' && <Roster data-id="element-14" />}
            {armsPage === 'attendance' && <EmployeeAttendance loggedInUser={loggedInUser} data-id="element-15" />}
            {armsPage === 'manual' && <div className="space-y-6" data-id="element-16">
                <AttendanceManagement data-id="element-17" />
                <div className="px-6 pb-6 max-w-7xl mx-auto" data-id="element-18">
                  <div className="border-t border-gray-200 pt-6" data-id="element-19">
                    <h2 className="text-xl font-bold text-gray-800 mb-6 px-6" data-id="element-20">
                      Manual Entry & Upload
                    </h2>
                    <ManualAttendance data-id="element-21" />
                  </div>
                </div>
              </div>}
            {armsPage === 'payroll' && <Payroll data-id="element-22" />}

            {(armsPage === 'reports' || armsPage === 'settings') && <div className="p-12 flex flex-col items-center justify-center h-full text-gray-400" data-id="element-23">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-24">
                  <span className="text-2xl" data-id="element-25">🚧</span>
                </div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-26">
                  Coming Soon
                </h2>
                <p data-id="element-27">This module is currently under development.</p>
              </div>}
          </main>
        </div>
      </div>;
  }
  // ERPLogiPark Dashboard
  return <div className="flex flex-col h-screen w-full bg-white overflow-hidden font-sans" data-id="element-28">
      <Header data-id="element-29" />

      <div className="flex flex-1 overflow-hidden" data-id="element-30">
        <Sidebar activePage={erpPage} onMenuClick={handleErpMenuClick} data-id="element-31" />

        <main className="flex-1 overflow-y-auto bg-white p-6" data-id="element-32">
          {/* Top Status Bar */}
          <div className="flex items-center justify-between mb-6" data-id="element-33">
            <div className="flex items-center gap-2" data-id="element-34">
              <div className="w-3 h-3 rounded-full bg-green-600" data-id="element-35"></div>
              <h2 className="text-lg font-bold text-gray-900" data-id="element-36">
                {getErpPageTitle()}
              </h2>
            </div>

            <div className="flex items-center gap-2" data-id="element-37">
              <button className="text-orange-500 hover:text-orange-600 transition-colors" data-id="element-38">
                <CheckSquare size={28} fill="#F97316" color="white" data-id="element-39" />
              </button>
              <div className="w-7 h-7 bg-[#0047AB] rounded-sm cursor-pointer hover:opacity-90" data-id="element-40"></div>
              <div className="w-7 h-7 bg-[#22C55E] rounded-sm cursor-pointer hover:opacity-90" data-id="element-41"></div>
              <div className="w-7 h-7 bg-[#EF4444] rounded-sm cursor-pointer hover:opacity-90" data-id="element-42"></div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full" data-id="element-43">
            {erpPage === 'operations' ? <OperationsCharges data-id="element-44" /> : erpPage === 'otherServices' ? <OtherServicesDashboard data-id="element-45" /> : erpPage === 'serviceRegistration' ? <ServiceRegistration data-id="element-46" /> : erpPage === 'applyService' ? <ApplyService data-id="element-47" /> : <TrackStatusDashboard data-id="element-48" />}
          </div>
        </main>
      </div>

      {/* Check-In Popup */}
      {showCheckIn && loggedInUser && <CheckInPopup user={loggedInUser} onClose={() => setShowCheckIn(false)} data-id="element-49" />}
    </div>;
}